import type { Coverage, CoverageEligibilityResponse } from '@medplum/fhirtypes';
import { BENEFIT_NETWORK_SYSTEM } from '@unify/fhir-model/constants';
import { getVerifiedBillingServiceCode } from '@unify/fhir-model/resources/coverage-eligibility';

import type { InsurancePriority, InsuranceEntry, VerificationEntry, VerificationStatus } from './types';

function insuranceMatchingCoverages(
  cer: CoverageEligibilityResponse,
  coverages: Coverage[],
): NonNullable<CoverageEligibilityResponse['insurance']>[number] | undefined {
  const ids = new Set(coverages.map((c) => c.id).filter(Boolean));
  const items = cer.insurance ?? [];
  return items.find((ins) => {
    const id = ins.coverage?.reference?.split('/')[1];
    return Boolean(id && ids.has(id));
  });
}

function copayCentsForNetwork(
  insurance: NonNullable<CoverageEligibilityResponse['insurance']>[number] | undefined,
  network: 'in' | 'out',
): number | undefined {
  const copay = insurance?.item
    ?.filter((item) =>
      item.network?.coding?.some(
        (coding) => coding.system === BENEFIT_NETWORK_SYSTEM && coding.code === network,
      ),
    )
    .flatMap((item) => item.benefit ?? [])
    .find((benefit) => benefit.type?.coding?.some((coding) => coding.code === 'copay'));
  return copay?.allowedMoney?.value != null ? Math.round(copay.allowedMoney.value * 100) : undefined;
}

/** Map FHIR CoverageEligibilityResponse → VerificationsSection row. */
export function cerToVerificationEntry(
  cer: CoverageEligibilityResponse,
  coverages: Coverage[],
): VerificationEntry | null {
  const insurance = insuranceMatchingCoverages(cer, coverages);
  if (!insurance) return null;
  const covRef = insurance.coverage?.reference ?? '';
  const covId = covRef.split('/')[1];
  const cov = coverages.find((c) => c.id === covId);

  const items = insurance?.item ?? [];
  const copay = items
    .flatMap((it) => it.benefit ?? [])
    .find((b) => b.type?.coding?.some((c) => c.code === 'copay'));
  const coinsurance = items
    .flatMap((it) => it.benefit ?? [])
    .find((b) => b.type?.coding?.some((c) => c.code === 'copay-percent'));
  const deductible = items
    .flatMap((it) => it.benefit ?? [])
    .find((b) => b.type?.coding?.some((c) => c.code === 'deductible'));

  const status: VerificationStatus =
    cer.outcome === 'complete' ? 'current' : cer.outcome === 'queued' ? 'pending' : 'deprecated';

  return {
    id: cer.id ?? `cer-${cer.created ?? 'unknown'}`,
    insuranceId: covId ?? '',
    provider: cov?.payor?.[0]?.display ?? '—',
    priority:
      cov?.order === 2 ? 'secondary' : cov?.order === 3 ? 'tertiary' : 'primary',
    status,
    copayCents:
      copay?.allowedMoney?.value != null ? Math.round(copay.allowedMoney.value * 100) : undefined,
    coinsurancePercent: coinsurance?.allowedUnsignedInt,
    deductibleCents:
      deductible?.allowedMoney?.value != null
        ? Math.round(deductible.allowedMoney.value * 100)
        : undefined,
    innCopayCents: copayCentsForNetwork(insurance, 'in'),
    oonCopayCents: copayCentsForNetwork(insurance, 'out'),
    billingCode: getVerifiedBillingServiceCode(cer),
    inforce: insurance?.inforce,
    verifiedAt: cer.created,
  };
}

export function formatMoney(cents: number): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
}

export function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US');
  } catch {
    return iso;
  }
}

export function priorityLabel(p: InsurancePriority): string {
  if (p === 'primary') return 'Primary';
  if (p === 'secondary') return 'Secondary';
  return 'Tertiary';
}

export function prioritySortKey(p: InsurancePriority): number {
  if (p === 'primary') return 1;
  if (p === 'secondary') return 2;
  return 3;
}

export function maskId(value: string): string {
  if (!value) return '—';
  const trimmed = value.trim();
  if (trimmed.length <= 4) return `••••${trimmed}`;
  return `••••${trimmed.slice(-4)}`;
}

export function verificationBadge(status: InsuranceEntry['verificationStatus']) {
  if (status === 'verified') return { color: 'green' as const, label: 'Verified' };
  if (status === 'needs_info') return { color: 'yellow' as const, label: 'Needs info' };
  return { color: 'gold' as const, label: 'Verification pending' };
}

export function verificationStatusBadge(status: VerificationStatus): { color: 'green' | 'gold' | 'gray'; label: string } {
  if (status === 'current') return { color: 'green', label: 'Current' };
  if (status === 'pending') return { color: 'gold', label: 'Pending' };
  return { color: 'gray', label: 'Deprecated' };
}
